import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  blockDataRowCountState,
  blockFonts,
  blockGridColumnWidthsState,
  blockGridStyleState,
  blockLimitedRequestSubjects,
  blockTrailerHeightState,
  convertApiSubjectToStudioSubject,
  getCustomFontSize,
  isReportState,
  recoilBlockContentSize,
  showBlockGainLoss,
  showBlockNetTaxImpact,
  viewDataGridSizeType,
} from 'venn-state';
import { DataGridSizeType } from '../../../studio-blocks/components/grid/types';
import { useAppPrintMode } from '../../../print/AppPrintMode';
import { formatData } from '../../../data-grid/gridUtils';
import { TAX_IMPACT_MAX_CELL_HORIZONTAL_PADDING_PX, useTaxImpact } from './logic/useTaxImpact';
import { isEqual } from 'lodash';
import { Numbers } from 'venn-utils';
import { useTaxRate } from '../../../studio-blocks/logic/useTaxRate';
import { useCommonTreeGridProps } from '../../../studio-blocks/logic/columnUtils';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { autoGroupColumnDefStaticConstants, BOLD_CLASS, useSetBlockSize } from '../../../studio-blocks';
import { DATA_TYPE_EXCEL_STYLES } from '../../../studio-blocks/logic/customBlockUtils';
import { GridWrapper } from '../../../studio-blocks/common';
import { useBlockContext } from '../../contexts/block-context';
import ValueCellRenderer from '../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getDefaultValueFormat } from '../../../studio-blocks/logic/gridStyling';
import type { CellClassParams, CellStyle, ColDef, ICellRendererParams } from 'ag-grid-community';
import { Icon, Tooltip, TooltipPosition } from 'venn-ui-kit';
import { useIsTruncated } from './logic/useIsTruncated';
import type { TaxImpactRow } from './logic/buildTaxImpactTree';

const treeGridOverrides: ColDef<TaxImpactRow> = {
  cellRenderer: ValueCellRenderer,
  valueFormatter: getDefaultValueFormat,
  cellClassRules: {
    [BOLD_CLASS]: (params: CellClassParams<TaxImpactRow>) => !!params.data?.isStrategy,
  },
};

const MAX_NAME_COLUMN_WIDTH_PX = 300;

const AUTO_SIZE_PADDING_PX = 4;

const INCONSISTENT_PRICING_WARNING =
  'The lots for this investment have inconsistent pricing. Fix the inconsistencies in Edit Tax Information.';

const TaxImpactNameRenderer = ({ value, data }: ICellRendererParams<TaxImpactRow>) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>({ text: value });
  return (
    <span className="flex min-w-0 items-center gap-[4px]">
      <Tooltip usePortal isHidden={!isTruncated} content={value} className="min-w-0 max-w-full">
        <span ref={ref} className="block truncate">
          {value}
        </span>
      </Tooltip>
      {data?.hasInconsistentPricing && (
        <Tooltip usePortal position={TooltipPosition.Top} maxWidth={300} content={INCONSISTENT_PRICING_WARNING}>
          <Icon
            type="exclamation-triangle"
            prefix="far"
            className="text-[rgb(var(--color-warning))]"
            data-testid="qa-inconsistent-pricing-warning"
          />
        </Tooltip>
      )}
    </span>
  );
};

// Built lazily because autoGroupColumnDefStaticConstants is still undefined at module load in some import cycles
const getTaxImpactAutoGroupColumnDef = (): ColDef => ({
  ...autoGroupColumnDefStaticConstants,
  minWidth: 100,
  maxWidth: MAX_NAME_COLUMN_WIDTH_PX,
  cellRendererParams: {
    ...autoGroupColumnDefStaticConstants.cellRendererParams,
    innerRenderer: TaxImpactNameRenderer,
  },
  cellStyle: (params): CellStyle => {
    const level = params.node.isRowPinned() ? 0 : params.node.level;
    return {
      paddingLeft: `calc(${level} * var(--venn-indent-per-auto-group-level) + var(--ag-cell-horizontal-padding) - 1px)`,
      ...(params.data?.isStrategy ? { fontWeight: 'bold' } : {}),
    };
  },
});

export const TaxImpactContent = ({ id }: { id: string }) => {
  const { setTrailerElement, sliceWindow } = useBlockContext();
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const isReport = useRecoilValue(isReportState);
  const isCompact = useRecoilValue(viewDataGridSizeType(id)) === DataGridSizeType.COMPACT;
  const showGainLoss = useRecoilValue(showBlockGainLoss(id));
  const showNetTaxImpact = useRecoilValue(showBlockNetTaxImpact(id));
  const gridStyle = useRecoilValue(blockGridStyleState(id));
  const titleStyle = useRecoilValue(blockFonts.blockTitle(id));
  const subjects = useRecoilValue(blockLimitedRequestSubjects(id));
  const cellHorizontalPaddingPx = Math.min(
    gridStyle.cellHorizontalPaddingPx,
    TAX_IMPACT_MAX_CELL_HORIZONTAL_PADDING_PX,
  );

  const primarySubject = subjects[0] && convertApiSubjectToStudioSubject(subjects[0]);
  const [taxRate] = useTaxRate(primarySubject);

  const { rowData, columnDefs, pinnedBottomRowData, totalImpact } = useTaxImpact(id);

  const commonTreeGridProps = useCommonTreeGridProps(treeGridOverrides);

  const autoGroupColumnDef = useMemo(() => getTaxImpactAutoGroupColumnDef(), []);

  const trailerHeightPx = useRecoilValue(blockTrailerHeightState(id));
  const setRawBlockContentSize = useSetBlockSize(recoilBlockContentSize.rawState(id));
  const lastGridSizeRef = useRef<{ height: number; width: number } | undefined>(undefined);

  const onGridSizeChange = useCallback(
    (height: number, width: number) => {
      lastGridSizeRef.current = { height, width };
      setRawBlockContentSize({ bounds: { height: height + trailerHeightPx, width } });
    },
    [setRawBlockContentSize, trailerHeightPx],
  );

  useEffect(() => {
    // AG Grid only invokes the size callback on grid resize events, so toggling a footer switch alone
    // would otherwise never re-report and the overflow indicator would not update.
    const lastGridSize = lastGridSizeRef.current;
    if (!lastGridSize) return;
    setRawBlockContentSize({
      bounds: { height: lastGridSize.height + trailerHeightPx, width: lastGridSize.width },
    });
  }, [trailerHeightPx, setRawBlockContentSize]);

  // A windowed slice shows only mid-table rows; the pinned totals and trailer belong to the end of the
  // table, and AG-Grid would otherwise pin the totals to the bottom of every window.
  const isWindowedSlice = !!sliceWindow;
  const showTrailerAndPinnedTotals = !isWindowedSlice;

  const setGridColumnWidths = useSetRecoilState(blockGridColumnWidthsState(id));
  const onColumnWidthsMeasured = useCallback(
    (widths: Record<string, number>) => {
      setGridColumnWidths((previous) => (isEqual(previous, widths) ? previous : widths));
    },
    [setGridColumnWidths],
  );

  const setDataRowCount = useSetRecoilState(blockDataRowCountState(id));
  useEffect(() => {
    setDataRowCount((prev) => (prev === rowData.length ? prev : rowData.length));
  }, [rowData.length, setDataRowCount]);

  return (
    <>
      <GridWrapper isReport={isReport}>
        <ExportableGrid
          {...commonTreeGridProps}
          selectedRefId={id}
          rowData={rowData}
          columnDefs={columnDefs}
          isCompact={isCompact}
          inPrintMode={inPrintModeOrReportLab}
          exportable
          excelStyles={DATA_TYPE_EXCEL_STYLES}
          pinnedBottomRowData={showTrailerAndPinnedTotals ? pinnedBottomRowData : undefined}
          autoGroupColumnDef={autoGroupColumnDef}
          treeData
          fitAutoGroupColumnToWidth
          autoSizePadding={AUTO_SIZE_PADDING_PX}
          cellHorizontalPaddingPx={cellHorizontalPaddingPx}
          onGridSizeChange={isWindowedSlice ? undefined : onGridSizeChange}
          fixedHeightWindow={sliceWindow}
          columnWidths={sliceWindow?.columnWidths}
          onColumnWidthsMeasured={isWindowedSlice ? undefined : onColumnWidthsMeasured}
        />
      </GridWrapper>
      {showTrailerAndPinnedTotals && (
        <div ref={setTrailerElement}>
          <div
            style={{ fontSize: gridStyle.fontSize, paddingLeft: `${cellHorizontalPaddingPx}px` }}
            className="py-[6px] bg-venn-grey-10 border-solid border-y-[1px] border-x-0 border-venn-grey-30"
          >
            {`Federal Capital Gains Tax Rates: Short Term: ${Numbers.safeFormatPercentage(taxRate.fedStRate)}, Long Term: ${Numbers.safeFormatPercentage(taxRate.fedLtRate)}; State Tax Rate: ${Numbers.safeFormatPercentage(taxRate.stateRate)}; NIIT Rate: ${Numbers.safeFormatPercentage(taxRate.niitRate)}`}
          </div>
          <div className="flex flex-col divide-solid divide-y-[1px] divide-x-0 divide-venn-grey-30">
            {showGainLoss && (
              <div className="px-[15px] py-[16px] flex flex-col gap-[4px] items-center">
                <span className="font-bold" style={{ fontSize: getCustomFontSize(titleStyle) }}>
                  Gain/Loss
                </span>
                <span
                  className="flex divide-solid divide-x-[1px] divide-y-0 divide-venn-grey-30"
                  style={{ fontSize: gridStyle.fontSize }}
                >
                  <span className="px-[8px]">
                    <span className="font-bold">Long-Term Capital Gains: </span>
                    {formatData({ num: totalImpact.shortTermGainLoss, dataType: 'CURRENCY' })}
                  </span>
                  <span className="px-[8px]">
                    <span className="font-bold">Short-Term Capital Gains: </span>
                    {formatData({ num: totalImpact.longTermGainLoss, dataType: 'CURRENCY' })}
                  </span>
                  <span className="px-[8px]">
                    <span className="font-bold">Net Capital Gains: </span>
                    {formatData({ num: totalImpact.netCapitalGains, dataType: 'CURRENCY' })}
                  </span>
                </span>
              </div>
            )}
            {showNetTaxImpact && (
              <div className="px-[15px] py-[16px] flex flex-col gap-[4px] items-center">
                <span className="font-bold" style={{ fontSize: getCustomFontSize(titleStyle) }}>
                  Net Estimated Tax Impact
                </span>
                <span
                  className="flex divide-solid divide-x-[1px] divide-y-0 divide-venn-grey-30"
                  style={{ fontSize: gridStyle.fontSize }}
                >
                  <span className="px-[8px]">
                    <span className="font-bold">Long-Term Capital Gains Tax: </span>
                    {formatData({ num: totalImpact.longTermTax, dataType: 'CURRENCY' })}
                  </span>
                  <span className="px-[8px]">
                    <span className="font-bold">Short-Term Capital Gains Tax: </span>
                    {formatData({ num: totalImpact.shortTermTax, dataType: 'CURRENCY' })}
                  </span>
                  <span className="px-[8px]">
                    <span className="font-bold">Net Capital Gains Tax: </span>
                    {formatData({ num: totalImpact.netTax, dataType: 'CURRENCY' })}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
